import { Component, OnInit, OnDestroy, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { CommonModule, TitleCasePipe } from '@angular/common';
import { Router } from '@angular/router';
import { Observable, Subscription, interval, Subject } from 'rxjs';
import { takeUntil, debounceTime, tap } from 'rxjs/operators';
import { WsLiveServiceService } from '../../shared/services/ws-live-service.service';
import { LiveServiceHttpService } from '../../shared/services/live-service-http.service';
import { HospitalTheatreCaseModel } from '../../shared/models/hospitalTheatreCaseModel';
import { NoDataPipe } from '../../shared/pipes/no-data.pipe';
import { UnderscoreToTitlePipe } from '../../shared/pipes/underscore-to-title.pipe';
import moment from 'moment';

@Component({
  selector: 'app-hospital-theatre-cases-feed',
  standalone: true,
  imports: [CommonModule, TitleCasePipe, NoDataPipe, UnderscoreToTitlePipe],
  templateUrl: './hospital-theatre-cases-feed.component.html',
  styleUrl: './hospital-theatre-cases-feed.component.scss'
})
export class HospitalTheatreCasesFeedComponent implements OnInit, OnDestroy, AfterViewInit {

  @ViewChild('feedWrapper') feedWrapper!: ElementRef;
  @ViewChild('feedRows') feedRows!: ElementRef;

  public today: Date = new Date();
  public theatreCases: HospitalTheatreCaseModel[] = [];
  public displayCases: HospitalTheatreCaseModel[][] = [];
  public displayOffset: number = 0;

  private feedTickRate: number = 60000;
  private feedInterval$!: Observable<number>;
  private destroyFeedInterval$ = new Subject<void>();
  private unsubscribe: Subscription[] = [];

  constructor(
    private router: Router,
    private wsLiveServiceService: WsLiveServiceService,
    private liveServiceHttpService: LiveServiceHttpService,
    private elementRef: ElementRef
  ) {
  }

  ngOnInit() {
    this.initFeed();
    // Handle running time display for the feed
    const currSecOffset = 60 - new Date().getSeconds();
    setTimeout(() => this.today = new Date(), currSecOffset * 1000);
    this.unsubscribe.push(
      interval(60000).pipe(
        debounceTime(currSecOffset * 1000),
        tap(() => this.today = new Date())
      ).subscribe()
    );
  }

  ngAfterViewInit() {
    // See if we can hook this into a render event to refresh
    if (this.feedWrapper && this.feedRows) {
      setTimeout(() => this.setFeedFlow(), 0); // Run first time
      this.feedInterval$ = interval(this.feedTickRate);
      this.feedInterval$.pipe(takeUntil(this.destroyFeedInterval$)).subscribe(() => {
        this.setFeedFlow(); // Run every tick again
      });
    }
  }

  ngOnDestroy() {
    this.destroyFeedInterval$.next();
    this.disconnectSocket();
    this.unsubscribe.forEach(s => s.unsubscribe());
  }

  public closeFeed() {
    this.liveServiceHttpService.blacklistToken().subscribe({
      next: async res => {
        this.router.navigate(['/activate']);
      }
    })
  }

  private initFeed() {
    this.fetchLiveFeeds();
    // Add sub for updates that are pushed
    this.unsubscribe.push(
      this.wsLiveServiceService.onLiveFeedUpdateEvent().subscribe(feedUpdates => {
        // Handle each update item, either new or updated item
        feedUpdates.forEach(update => {
          const caseIdx = this.theatreCases.findIndex(c => c.caseid === update.caseid);
          if (caseIdx !== -1) {
            this.theatreCases[caseIdx] = update;
          } else {
            this.theatreCases.push(update);
          }
        });

        this.formatFeed();
        setTimeout(() => this.setFeedFlow(), 100);
      })
    );
  }

  private fetchLiveFeeds() {
    // Subscribe to fetch list event and pull, once pulled clear
    const fetchFeed = this.wsLiveServiceService.onLiveFeedFetchEvent().subscribe(liveFeeds => {
      fetchFeed.unsubscribe();
      this.theatreCases = liveFeeds;
      this.formatFeed();
      setTimeout(() => this.setFeedFlow(), 100);
    });
    this.wsLiveServiceService.emitFetchLiveFeedEvent();
  }

  private disconnectSocket() {
    this.wsLiveServiceService.disconnect();
  }

  private formatFeed() {
    // Sort by date and time
    this.theatreCases.sort((a, b) => {
      const aStartDate = moment(a.scheduled_time_start);
      const bStartDate = moment(b.scheduled_time_start);

      if (aStartDate.isBefore(bStartDate)) return -1;

      // If the start times are the same then order by later end time
      if (aStartDate.isSame(bStartDate)) {
        const aEndDate = moment(a.scheduled_time_end);
        const bEndDate = moment(b.scheduled_time_end);
        if (aEndDate.isBefore(bEndDate)) return -1;
        if (aEndDate.isSame(bEndDate)) return 0;
        return 1
      };

      return 1;
    });

    // Group theatre cases by start time blocks as ORDERED ARRAYS
    this.displayCases = this.theatreCases.reduce((acc, curr) => {
      const previous = acc.slice(-1);
      if (previous.length === 1 && previous[0][0].scheduled_time_start === curr.scheduled_time_start) {
        acc[acc.length - 1].push(curr);
      } else {
        acc.push([curr]);
      }
      return acc;
    }, [] as HospitalTheatreCaseModel[][]);
  }

  private setFeedFlow() {
    // Get current time
    const currentTime = moment();
    let offset = this.feedWrapper.nativeElement.getBoundingClientRect().height / 2;

    // Get current list positioning
    for (let blockIndex = 0; blockIndex < this.displayCases.length; blockIndex++) {
      const currentCase = this.displayCases[blockIndex];
      const nextCase = this.displayCases[blockIndex + 1];

      // We get our start and end from the first and last items of the block array
      const blockStart = moment(currentCase[0].scheduled_time_start);

      // use next block start if available
      const blockEnd = nextCase ? moment(nextCase[nextCase.length - 1].scheduled_time_start) : moment(currentCase[currentCase.length - 1].scheduled_time_end);

      // Current time falls in this block
      const blockHtmlElement = document.getElementById(`block-${blockIndex}`);

      // Shifts initial offset up by current block height
      if (blockHtmlElement) {
        // Some time after start of block
        if (currentTime.isSameOrAfter(blockStart)) {
          // Inside block
          if (currentTime.isSameOrBefore(blockEnd)) {
            offset -= (1 - (blockEnd.diff(currentTime) / blockEnd.diff(blockStart))) * blockHtmlElement.getBoundingClientRect().height;
            break;
          }
          offset -= blockHtmlElement.getBoundingClientRect().height;
        }
      }
    }
    this.displayOffset = offset;
  }
}
